/* eslint-disable max-lines-per-function */
import React, { useState, useEffect, useRef } from "react";
import { cn } from "tailwind-variants";
import { isMobile, isTouchDevice } from "common/utils/browser";
import { BACKGROUND_TYPE, COLORS } from "components/jiffy/design-lab/shared/constants/index";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import { useDtfStore } from "../store";
import ImageCompareLoadingContent from "./ImageCompareLoadingContent.vue";
import "./ImageCompare.scss";

function ImageCompare({
  beforeImage = "",
  afterImage = "",
  beforeText = undefined,
  afterText = undefined,
  isInline = false,
  isFullWidth = false,
  isArtwork = false,
  showMagnifier = false,
  magnifierPosition = "left",
  sliderPosition = 50,
  imageBackground = "transparent",
  enhancementProcessing = false,
  selectedLabel = null,
  enhancementTier = null,
  isModal = false,
  onCanvasLoaded,
  slots,
  ...restProps
}) {
  const [mousePosition, setMousePosition] = useState({ xPerc: 50, yPerc: 50 });
  const [isHovering, setIsHovering] = useState(false);
  const [imageMaxWidth, setImageMaxWidth] = useState("unset");
  const [imageHeight, setImageHeight] = useState("unset");
  const [disableSlider, setDisableSlider] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [originalWidth, setOriginalWidth] = useState(0);
  const [originalHeight, setOriginalHeight] = useState(0);
  const [afterImageIntrinsicHeight, setAfterImageIntrinsicHeight] = useState(0);
  const [afterImageIntrinsicWidth, setAfterImageIntrinsicWidth] = useState(0);

  const store = useDtfStore();
  const imageContainerRef = useRef(null);
  const afterImageElementRef = useRef(null);

  function isSticker() {
    return store.isSticker;
  }

  function isStickerSample() {
    return store.isStickerSample;
  }

  function isStandardArtwork() {
    return store.isStandardArtwork;
  }

  function backgroundType() {
    return store.backgroundType;
  }

  function positionPercentageX() {
    return `${mousePosition.xPerc}%`;
  }

  function positionPercentageY() {
    return `${mousePosition.yPerc}%`;
  }

  function beforeImageCSS() {
    return `url(${beforeImage})`;
  }

  function afterImageCSS() {
    return `url(${afterImage})`;
  }

  function sliderVerticalPosition() {
    return "90%";
  }

  function imageMaxHeightComputed() {
    return isArtwork ? "unset" : "80vh";
  }

  function isTappable() {
    return isTouchDevice() || isMobile();
  }

  function isTransparent() {
    return imageBackground === "transparent";
  }

  function computedImageBackground() {
    if (backgroundType() === BACKGROUND_TYPE.GREEN) {
      return COLORS.GREEN_BACKGROUND;
    }
    return imageBackground;
  }

  function transparentBackgroundClasses() {
    return cn(
      isTransparent() && isCheckeredBackground() && "image-compare__transparent-background",
      isStandardArtwork() &&
        isCheckeredBackground() &&
        "image-compare__transparent-background--standard-artwork"
    );
  }

  function isImageSame() {
    return !enhancementProcessing && beforeImage === afterImage;
  }

  function hideSlider() {
    return (
      isSticker() ||
      isStickerSample() ||
      disableSlider ||
      isImageSame() ||
      !imageLoaded ||
      enhancementProcessing
    );
  }

  function showFullWidthArtwork() {
    if (isFullWidth) {
      return true;
    }
    return !isArtwork && hideSlider() && isTappable();
  }

  function isCheckeredBackground() {
    return backgroundType() === BACKGROUND_TYPE.CHECKERED;
  }

  function setPreviewCanvasSize(...args) {
    return store.setPreviewCanvasSize(...args);
  }

  function onBeforeImageLoad(event) {
    const image = event.target;
    setOriginalWidth(image.naturalWidth);
    setOriginalHeight(image.naturalHeight);
    setDisableSlider(!image.complete);
    if (!image.complete) return;
    setImageMaxWidth(image.naturalWidth);
    if (isArtwork) return;
    if (image.naturalHeight < 1000) {
      setImageHeight(`80vh`);
    }
  }

  function onAfterImageLoad(event) {
    const image = event.target;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => initializeImage(image));
    });
  }

  function initializeImage(image) {
    if (!enhancementProcessing) {
      setAfterImageIntrinsicHeight(image.naturalHeight);
      setAfterImageIntrinsicWidth(image.naturalWidth);
    }
    if (!imageContainerRef.current) {
      return;
    }
    const { width, height } = imageContainerRef.current.getBoundingClientRect();
    setPreviewCanvasSize({
      width,
      height
    });
    setImageLoaded(true);
    if (onCanvasLoaded) {
      onCanvasLoaded();
    }
  }

  function constrain(num, min = 0, max = 100) {
    return Math.min(Math.max(num, min), max);
  }

  function mouseOver() {
    setIsHovering(true);
  }

  function mouseOut() {
    setIsHovering(false);
  }

  function getCoordinates(event) {
    if (event.type === "touchmove") {
      const { touches, changedTouches } = event.originalEvent ?? event;
      const touch = touches[0] ?? changedTouches[0];
      return {
        clientX: touch.clientX,
        clientY: touch.clientY
      };
    }
    return {
      clientX: event.clientX,
      clientY: event.clientY
    };
  }

  function mouseMove(event) {
    if (disableSlider) return;
    requestAnimationFrame(() => {
      const { clientX, clientY } = getCoordinates(event);
      const { left, width, top, height } = event.target.getBoundingClientRect();
      const positionX = ((clientX - left) / width) * 100;
      const positionY = ((clientY - top) / height) * 100;
      setMousePosition({
        xPerc: constrain(positionX),
        yPerc: positionY
      });
    });
  }

  useEffect(() => {
    setMousePosition(prev => ({
      ...prev,
      xPerc: sliderPosition
    }));
  }, [sliderPosition]);

  return (
    <>
      <div
        className={cn("image-compare scope-302dbf5a")}
        ref={imageContainerRef}
        style={{
          "--302dbf5a-imageMaxHeight": imageMaxHeightComputed(),
          "--302dbf5a-imageMaxWidth": imageMaxWidth,
          "--302dbf5a-imageHeight": imageHeight,
          "--302dbf5a-positionPercentageX": positionPercentageX(),
          "--302dbf5a-computedImageBackground": computedImageBackground(),
          "--302dbf5a-sliderVerticalPosition": sliderVerticalPosition(),
          "--302dbf5a-positionPercentageY": positionPercentageY(),
          "--302dbf5a-beforeImageCSS": beforeImageCSS(),
          "--302dbf5a-afterImageCSS": afterImageCSS()
        }}
        onMouseOver={mouseOver}
        onMouseMove={mouseMove}
        onTouchMove={mouseMove}
        onMouseOut={mouseOut}
        data-testid="image-compare"
      >
        <div className={cn("image-compare__image-container scope-302dbf5a")}>
          {(beforeText || slots?.beforeText) && !isInline ? (
            <div
              className={cn("image-compare__label image-compare__label--before scope-302dbf5a", {
                "image-compare__label--floating": !isInline,
                "image-compare__label--selected": selectedLabel === "before"
              })}
            >
              {slots?.beforeText?.() ?? <>{beforeText}</>}
            </div>
          ) : null}
          {(afterText || slots?.afterText) && !isInline ? (
            <div
              className={cn("image-compare__label image-compare__label--after scope-302dbf5a", {
                "image-compare__label--floating": !isInline,
                hidden: hideSlider(),
                "image-compare__label--selected": selectedLabel === "after"
              })}
            >
              {slots?.afterText?.() ?? <>{afterText}</>}
            </div>
          ) : null}
          <div
            className={cn("image-compare__image-before scope-302dbf5a", {
              hidden: hideSlider(),
              ...transparentBackgroundClasses()
            })}
          >
            <img
              className={cn(
                "image-compare__slider-image image-compare__slider-image--before scope-302dbf5a"
              )}
              src={beforeImage}
              alt="Before Image"
              onLoad={onBeforeImageLoad}
            />
          </div>
          <div
            className={cn(
              "image-compare__image-after scope-302dbf5a",
              transparentBackgroundClasses()
            )}
          >
            <img
              className={cn(
                "image-compare__slider-image image-compare__slider-image--after scope-302dbf5a",
                {
                  "image-compare__slider-image--inline": isInline,
                  "image-compare__slider-image--placeholder": isArtwork && !imageLoaded,
                  "image-compare__slider-image--placeholder-modal":
                    isArtwork && !imageLoaded && isModal,
                  "image-compare__slider-image--artwork": isArtwork,
                  "image-compare__slider-image--full-width": showFullWidthArtwork()
                }
              )}
              ref={afterImageElementRef}
              src={afterImage}
              alt="After Image"
              {...restProps}
              onLoad={onAfterImageLoad}
            />

            {isStandardArtwork() && enhancementProcessing ? (
              <div className={cn("image-compare__enhancement-processing scope-302dbf5a")}>
                <ImageCompareLoadingContent
                  className="scope-302dbf5a"
                  enhancementTier={enhancementTier}
                  data-testid="image-compare-loading-content"
                />
              </div>
            ) : null}
          </div>
        </div>
        <input
          className={cn("image-compare__slider scope-302dbf5a", {
            "image-compare__slider--disabled": hideSlider()
          })}
          value={mousePosition.xPerc}
          onChange={e => {
            setMousePosition(prev => ({
              ...prev,
              xPerc: e.target.value
            }));
          }}
          type="range"
          min="0"
          max="100"
          aria-label="Percentage of before photo shown"
          data-testid="image-compare-slider"
        />

        <div
          className={cn("image-compare__slider-lines scope-302dbf5a", {
            hidden: hideSlider()
          })}
        >
          <div
            className={cn(
              "image-compare__slider-line image-compare__slider-line--top scope-302dbf5a"
            )}
            aria-hidden="true"
            data-testid="image-compare-slider-line"
          />

          <div
            className={cn("image-compare__slider-button scope-302dbf5a")}
            aria-hidden="true"
            data-testid="image-compare-slider-button"
          >
            <UseSvg
              className={cn("image-compare__slider-button-icon scope-302dbf5a")}
              icon="arrow-left"
              data-testid="image-compare-slider-button-icon"
            />

            <UseSvg
              className={cn("image-compare__slider-button-icon scope-302dbf5a")}
              icon="arrow-right"
              data-testid="image-compare-slider-button-icon-1"
            />
          </div>
          <div
            className={cn(
              "image-compare__slider-line image-compare__slider-line--bottom scope-302dbf5a"
            )}
            aria-hidden="true"
            data-testid="image-compare-slider-line-1"
          />
        </div>
      </div>
      {showMagnifier && isInline ? (
        <div
          className={cn("image-compare__label-container scope-302dbf5a", {
            hidden: hideSlider()
          })}
        >
          {beforeText ? (
            <div className={cn("image-compare__label image-compare__label--before scope-302dbf5a")}>
              {beforeText}
            </div>
          ) : null}
          {afterText ? (
            <div className={cn("image-compare__label image-compare__label--after scope-302dbf5a")}>
              {afterText}
            </div>
          ) : null}
        </div>
      ) : null}
      {showMagnifier && (isHovering || isInline) ? (
        <div
          className={cn("image-compare__magnifier-container scope-302dbf5a", {
            "image-compare__magnifier-container--inline": isInline,
            "image-compare__magnifier-container--right": magnifierPosition === "right",
            "image-compare__magnifier-container--left": magnifierPosition === "left",
            "image-compare__magnifier-container--transparent":
              isTransparent() && isCheckeredBackground(),
            hidden: hideSlider()
          })}
        >
          <div
            className={cn(
              "image-compare__magnifier-item image-compare__magnifier-item--before scope-302dbf5a"
            )}
          />

          <div
            className={cn(
              "image-compare__magnifier-item image-compare__magnifier-item--after scope-302dbf5a"
            )}
          />
        </div>
      ) : null}
    </>
  );
}

export default ImageCompare;
